/**
 * Kendo Helpers module
 * Simplified helpers for working with Kendo UI widgets
 * Contains only the functionality actually used in this project
 *
 * Refactored to eliminate code duplication using BaseKendoWidgetAdapter
 *
 * Grid helper now includes getDataItemFromEvent() for extracting dataItem from grid events/elements
 */

import { logWarning, safeExecute } from '../core/error-handler.js';
import eventBus, { EVENTS } from '../core/event-bus.js';

/**
 * Resolve a Kendo widget instance from a jQuery selector and Kendo data key
 * @param {string|HTMLElement|JQuery} selector
 * @param {string} kendoWidgetKey e.g. 'kendoGrid', 'kendoDropDownList'
 * @returns {any|null}
 */
function resolveWidget(selector, kendoWidgetKey) {
    return safeExecute(
        () => {
            if (!selector) return null;
            const $el = selector instanceof jQuery ? selector : $(selector);
            if ($el.length === 0) return null;
            return $el.data(kendoWidgetKey) || null;
        },
        null,
        null
    );
}

/**
 * Base class for all Kendo widget adapters
 * Eliminates code duplication by providing common functionality
 */
class BaseKendoWidgetAdapter {
    constructor(widget) {
        this.widget = widget;
    }

    /**
     * Check if the widget is available/initialized
     * @returns {boolean} True if widget is available
     */
    isAvailable() {
        return this.widget !== null && this.widget !== undefined;
    }

    /**
     * Get the current value of the widget
     * @returns {*} Current widget value
     */
    getValue() {
        return this.widget ? this.widget.value() : this.getDefaultValue();
    }

    /**
     * Set the value of the widget
     * @param {*} value Value to set
     * @returns {boolean} True if value was set successfully
     */
    setValue(value) {
        if (this.widget) {
            this.widget.value(value);
            return true;
        }
        return false;
    }

    /**
     * Check if the widget has an active value (for filtering)
     * @returns {boolean} True if widget has a non-empty value
     */
    hasValue() {
        const value = this.getValue();
        return this.isValueActive(value);
    }

    /**
     * Clear the widget's value
     * @returns {boolean} True if value was cleared successfully
     */
    clearValue() {
        return this.setValue(this.getDefaultClearValue());
    }

    /**
     * Bind an event handler to the widget
     * @param {string} eventName Event name (e.g., 'change', 'dataBound')
     * @param {Function} handler Event handler function
     */
    bind(eventName, handler) {
        if (this.widget && this.widget.bind) {
            this.widget.bind(eventName, handler);
        }
    }

    /**
     * Get the default value when widget is not available
     * Override in derived classes for specific logic
     * @returns {*} Default value
     */
    getDefaultValue() {
        return null;
    }

    /**
     * Get the default value to use when clearing
     * Override in derived classes for specific logic
     * @returns {*} Default clear value
     */
    getDefaultClearValue() {
        return '';
    }

    /**
     * Check if a value is considered "active" for filtering
     * Override in derived classes for specific logic
     * @param {*} value Value to check
     * @returns {boolean} True if value is active
     */
    isValueActive(value) {
        return value !== null && value !== undefined && value !== '';
    }
}

/**
 * Get dataItem from Kendo Grid row element
 * @param {Event|HTMLElement|null} eventOrElement - Event object, DOM element, or null
 * @returns {object|null} dataItem from Kendo Grid or null if not found
 */
function getDataItemFromGridRow(eventOrElement) {
    if (!eventOrElement) return null;

    // Get the target element
    const element = eventOrElement instanceof Event ? eventOrElement.target : eventOrElement;
    if (!element) return null;

    // Find the closest grid row (tr element with data-uid)
    const $row = $(element).closest('tr[data-uid]');
    if ($row.length === 0) {
        logWarning('Grid row not found from element', { element });
        return null;
    }

    // Find the grid
    const $grid = $row.closest('[data-role="grid"]');
    if ($grid.length === 0) {
        logWarning('Grid not found from row', { row: $row[0] });
        return null;
    }

    // Get Kendo Grid widget
    const grid = $grid.data('kendoGrid');
    if (!grid) {
        logWarning('Kendo Grid widget not found');
        return null;
    }

    // Get dataItem from the row
    const dataItem = grid.dataItem($row);
    if (!dataItem) {
        logWarning('DataItem not found from grid row');
        return null;
    }

    return dataItem;
}

/**
 * Grid helpers
 */
export const Grid = {
    /**
     * Create Grid widget wrapper instance
     * @param {string|HTMLElement|JQuery} selector
     * @returns {Object} Grid widget wrapper
     */
    ensure(selector) {
        const widget = resolveWidget(selector, 'kendoGrid');
        return {
            selector,
            widget,
            isAvailable: () => widget !== null,
            read: () => {
                return safeExecute(
                    () => {
                        if (!widget) {
                            logWarning(`Grid widget not available for selector: ${selector}`);
                            return false;
                        }
                        if (widget.dataSource) {
                            widget.dataSource.read();
                            eventBus.emit(EVENTS.GRID_REFRESHED, { selector: selector });
                            return true;
                        }
                        return false;
                    },
                    null,
                    false
                );
            },
            getSelectedRows: () => {
                return safeExecute(
                    () => {
                        if (!widget) return [];
                        if (typeof widget.select === 'function') {
                            const selectedElements = widget.select();
                            return selectedElements.map(element => widget.dataItem(element));
                        }
                        return [];
                    },
                    null,
                    []
                );
            }
        };
    },

    /**
     * Get dataItem from a grid row element or event
     * Useful for extracting data from click events or DOM elements within grid rows
     * @param {Event|HTMLElement} eventOrElement - Event object or DOM element from within a grid row
     * @returns {object|null} dataItem from Kendo Grid or null if not found
     */
    getDataItemFromEvent(eventOrElement) {
        return getDataItemFromGridRow(eventOrElement);
    }
};

/**
 * DropDownList helpers
 */
export const DropDownList = {
    ensure(selector) {
        const widget = resolveWidget(selector, 'kendoDropDownList');
        const adapter = new DropDownListAdapter(widget);

        return {
            selector,
            widget,
            isAvailable: () => adapter.isAvailable(),
            read: () => {
                return safeExecute(
                    () => {
                        if (!widget) {
                            logWarning(
                                `DropDownList widget not available for selector: ${selector}`
                            );
                            return false;
                        }
                        if (widget.dataSource) {
                            widget.dataSource.read();
                            return true;
                        }
                        return false;
                    },
                    null,
                    false
                );
            },
            getValue: () => adapter.getValue(),
            setValue: value => adapter.setValue(value),
            hasValue: () => adapter.hasValue(),
            clearValue: () => adapter.clearValue(),
            bind: (eventName, handler) => adapter.bind(eventName, handler)
        };
    }
};

/**
 * DropDownList specific adapter
 */
class DropDownListAdapter extends BaseKendoWidgetAdapter {
    isValueActive(value) {
        return value && value !== '';
    }
}

/**
 * MultiSelect helpers
 */
export const MultiSelect = {
    ensure(selector) {
        const widget = resolveWidget(selector, 'kendoMultiSelect');
        const adapter = new MultiSelectAdapter(widget);

        return {
            selector,
            widget,
            isAvailable: () => adapter.isAvailable(),
            read: () => {
                return safeExecute(
                    () => {
                        if (!widget) {
                            logWarning(
                                `MultiSelect widget not available for selector: ${selector}`
                            );
                            return false;
                        }
                        if (widget.dataSource) {
                            widget.dataSource.read();
                            return true;
                        }
                        return false;
                    },
                    null,
                    false
                );
            },
            getValue: () => adapter.getValue(),
            setValue: value => adapter.setValue(value),
            hasValue: () => adapter.hasValue(),
            clearValue: () => adapter.clearValue(),
            bind: (eventName, handler) => adapter.bind(eventName, handler)
        };
    }
};

/**
 * MultiSelect specific adapter
 */
class MultiSelectAdapter extends BaseKendoWidgetAdapter {
    getDefaultValue() {
        return [];
    }

    getDefaultClearValue() {
        return [];
    }

    isValueActive(value) {
        return Array.isArray(value) && value.length > 0;
    }
}

/**
 * ComboBox helpers
 */
export const ComboBox = {
    ensure(selector) {
        const widget = resolveWidget(selector, 'kendoComboBox');
        const adapter = new ComboBoxAdapter(widget);

        return {
            selector,
            widget,
            isAvailable: () => adapter.isAvailable(),
            read: () => {
                return safeExecute(
                    () => {
                        if (!widget) {
                            logWarning(`ComboBox widget not available for selector: ${selector}`);
                            return false;
                        }
                        if (widget.dataSource) {
                            widget.dataSource.read();
                            return true;
                        }
                        return false;
                    },
                    null,
                    false
                );
            },
            getValue: () => adapter.getValue(),
            setValue: value => adapter.setValue(value),
            hasValue: () => adapter.hasValue(),
            clearValue: () => adapter.clearValue(),
            bind: (eventName, handler) => adapter.bind(eventName, handler)
        };
    }
};

/**
 * ComboBox specific adapter
 */
class ComboBoxAdapter extends BaseKendoWidgetAdapter {
    isValueActive(value) {
        return value && value !== '';
    }
}

/**
 * Switch helpers
 */
export const Switch = {
    ensure(selector) {
        const widget = resolveWidget(selector, 'kendoSwitch');
        const adapter = new SwitchAdapter(widget);

        return {
            selector,
            widget,
            isAvailable: () => adapter.isAvailable(),
            getValue: () => adapter.getValue(),
            setValue: value => adapter.setValue(value),
            hasValue: () => adapter.hasValue(),
            clearValue: () => adapter.clearValue(),
            bind: (eventName, handler) => adapter.bind(eventName, handler)
        };
    }
};

/**
 * Switch specific adapter
 */
class SwitchAdapter extends BaseKendoWidgetAdapter {
    getDefaultValue() {
        return false;
    }

    getDefaultClearValue() {
        return false;
    }

    isValueActive(value) {
        return value !== null && value !== undefined;
    }
}

/**
 * DatePicker helpers
 */
export const DatePicker = {
    ensure(selector) {
        const widget = resolveWidget(selector, 'kendoDatePicker');
        const adapter = new DatePickerAdapter(widget);

        return {
            selector,
            widget,
            isAvailable: () => adapter.isAvailable(),
            getValue: () => adapter.getValue(),
            setValue: value => adapter.setValue(value),
            hasValue: () => adapter.hasValue(),
            clearValue: () => adapter.clearValue(),
            bind: (eventName, handler) => adapter.bind(eventName, handler)
        };
    }
};

/**
 * DatePicker specific adapter
 */
class DatePickerAdapter extends BaseKendoWidgetAdapter {
    getDefaultClearValue() {
        return null;
    }

    isValueActive(value) {
        return value !== null;
    }
}

/**
 * DateRangePicker helpers
 */
export const DateRangePicker = {
    ensure(selector) {
        const widget = resolveWidget(selector, 'kendoDateRangePicker');
        const adapter = new DateRangePickerAdapter(widget);

        return {
            selector,
            widget,
            isAvailable: () => adapter.isAvailable(),
            getValue: () => adapter.getValue(),
            setValue: value => adapter.setValue(value),
            hasValue: () => adapter.hasValue(),
            clearValue: () => adapter.clearValue(),
            bind: (eventName, handler) => adapter.bind(eventName, handler)
        };
    }
};

/**
 * DateRangePicker specific adapter
 */
class DateRangePickerAdapter extends BaseKendoWidgetAdapter {
    getDefaultClearValue() {
        return null;
    }

    isValueActive(value) {
        return value !== null && value !== undefined;
    }

    clearValue() {
        if (this.widget) {
            this.widget.range({ start: null, end: null });
            return true;
        }
        return false;
    }
}

// Default export
export default {
    Grid,
    DropDownList,
    MultiSelect,
    ComboBox,
    Switch,
    DatePicker,
    DateRangePicker
};
